import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, race, timer } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { ApiConfigService } from './api-config.service';
import { ApiAuthService } from './api-auth.service';

export interface ApiResponse<T> {
  data?: T;
  message?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private apiAuthService = inject(ApiAuthService);
  private apiConfig = inject(ApiConfigService);

  get baseUrl(): string {
    return this.apiConfig.getApiUrl();
  }

  /**
   * Get default headers with authentication
   * @param skipContentType - Skip setting Content-Type (e.g., for FormData uploads)
   */
  private getHeaders(skipContentType: boolean = false): HttpHeaders {
    let headers = new HttpHeaders();

    // Don't set Content-Type for FormData - browser will set it with boundary
    if (!skipContentType) {
      headers = headers.set('Content-Type', 'application/json');
    }

    const token = this.getAuthToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Get authentication token (JWT only)
   */
  private getAuthToken(): string | null {
    return this.apiAuthService.getToken();
  }

  private isVendorEarningsVerifyAccessUrl(url: string): boolean {
    return url.includes('vendor-earnings/verify-access') || url.includes('vendor-earnings%2Fverify-access');
  }

  /**
   * Handle HTTP errors
   * MEDIUM: Provide user-friendly error messages (less technical)
   */
  private handleError(error: HttpErrorResponse | any): Observable<never> {
    let errorMessage = 'Something went wrong. Please try again.';

    // MEDIUM: Handle timeout errors with user-friendly message
    if (error.status === 408 || error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      errorMessage = 'Request is taking too long. Please check your internet connection and try again.';
    } else if (error.error instanceof ErrorEvent) {
      // Client-side error
      if (error.error.message?.includes('Failed to fetch') || error.error.message?.includes('NetworkError')) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      } else {
        errorMessage = 'Unable to connect. Please check your internet and try again.';
      }
    } else {
      // Server-side error
      const status = error.status || 0;
      
      // MEDIUM: Map common status codes to user-friendly messages
      // MEDIUM: Handle partial network (request sent but no response) - status 0 usually means network error
      if (status === 0) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      } else if (status === 408) {
        errorMessage = 'Request timeout. Your action may have completed. Please refresh to check.';
      } else if (status === 401) {
        const reqUrl = (error as HttpErrorResponse).url ?? '';
        if (this.isVendorEarningsVerifyAccessUrl(reqUrl)) {
          errorMessage =
            typeof error.error?.message === 'string' && error.error.message.trim()
              ? error.error.message
              : 'Incorrect password.';
        } else {
          errorMessage = 'Your session has expired. Please log in again.';
          this.apiAuthService.clearToken();
        }
      } else if (status === 403) {
        const msg403 = error.error?.message;
        errorMessage =
          typeof msg403 === 'string' && msg403.trim()
            ? msg403
            : 'You don\'t have permission to perform this action.';
      } else if (status === 404) {
        errorMessage = 'The requested item was not found.';
      } else if (status >= 500) {
        errorMessage = 'Server error. Our team has been notified. Please try again in a few moments.';
      } else {
        // Handle validation errors (array format)
        if (Array.isArray(error.error?.message)) {
          errorMessage = error.error.message.join('. ');
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.error?.error) {
          errorMessage = error.error.error;
        } else if (error.message && !error.message.includes('HttpErrorResponse')) {
          errorMessage = error.message;
        }
      }
    }

    console.error('API Error:', error);
    // Log full error details for debugging (but don't show to user)
    
    // Create error with status code preserved
    const apiError = new Error(errorMessage) as any;
    apiError.status = error.status || 0;
    apiError.originalError = error;
    return throwError(() => apiError);
  }

  /**
   * GET request.
   * @param unwrap - When true (default), responses with a top-level `data` property are unwrapped to that value. Set to false for paginated responses so you receive { data, total, hasMore }.
   */
  get<T>(endpoint: string, params?: Record<string, any>, options?: { unwrap?: boolean }): Observable<T> {
    const unwrap = options?.unwrap !== false;
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }

    return this.http
      .get<ApiResponse<T> | T>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getHeaders(false),
        params: httpParams,
      })
      .pipe(
        // MEDIUM: Add 30-second timeout for slow API calls
        timeout(30000),
        map((response) => {
          if (!unwrap) return response as T;
          // Handle both wrapped and unwrapped responses
          if (response && typeof response === 'object' && 'data' in response) {
            return (response as ApiResponse<T>).data as T;
          }
          return response as T;
        }),
        catchError((error) => {
          // MEDIUM: Provide user-friendly timeout error message
          if (error.name === 'TimeoutError' || error.message?.includes('Timeout')) {
            const timeoutError = new Error('Request is taking too long. Please check your connection and try again.') as any;
            timeoutError.status = 408;
            return this.handleError(timeoutError);
          }
          return this.handleError(error);
        }),
      );
  }

  /**
   * POST request
   */
  post<T>(endpoint: string, body: any, params?: Record<string, any>): Observable<T> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }

    // Detect FormData to skip Content-Type header
    const isFormData = body instanceof FormData;
    const headers = this.getHeaders(isFormData);

    return this.http
      .post<ApiResponse<T> | T>(`${this.baseUrl}/${endpoint}`, body, {
        headers,
        params: httpParams,
      })
      .pipe(
        // Native Capacitor multipart uploads can be slow for large original images.
        timeout(isFormData ? 300000 : 30000),
        map((response) => {
          if (response && typeof response === 'object' && 'data' in response) {
            return (response as ApiResponse<T>).data as T;
          }
          return response as T;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError' || error.message?.includes('Timeout')) {
            const timeoutError = new Error('Request is taking too long. Please check your connection and try again.') as any;
            timeoutError.status = 408;
            return this.handleError(timeoutError);
          }
          return this.handleError(error);
        }),
      );
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, body: any): Observable<T> {
    const isFormData = body instanceof FormData;
    const headers = this.getHeaders(isFormData);
    
    return this.http
      .put<ApiResponse<T> | T>(`${this.baseUrl}/${endpoint}`, body, {
        headers,
      })
      .pipe(
        timeout(30000),
        map((response) => {
          if (response && typeof response === 'object' && 'data' in response) {
            return (response as ApiResponse<T>).data as T;
          }
          return response as T;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError' || error.message?.includes('Timeout')) {
            const timeoutError = new Error('Request is taking too long. Please check your connection and try again.') as any;
            timeoutError.status = 408;
            return this.handleError(timeoutError);
          }
          return this.handleError(error);
        }),
      );
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body: any): Observable<T> {
    const isFormData = body instanceof FormData;
    const headers = this.getHeaders(isFormData);
    
    return this.http
      .patch<ApiResponse<T> | T>(`${this.baseUrl}/${endpoint}`, body, {
        headers,
      })
      .pipe(
        timeout(30000),
        map((response) => {
          if (response && typeof response === 'object' && 'data' in response) {
            return (response as ApiResponse<T>).data as T;
          }
          return response as T;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError' || error.message?.includes('Timeout')) {
            const timeoutError = new Error('Request is taking too long. Please check your connection and try again.') as any;
            timeoutError.status = 408;
            return this.handleError(timeoutError);
          }
          return this.handleError(error);
        }),
      );
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string): Observable<T> {
    return this.http
      .delete<ApiResponse<T> | T>(`${this.baseUrl}/${endpoint}`, {
        headers: this.getHeaders(false),
      })
      .pipe(
        timeout(30000),
        map((response) => {
          if (response && typeof response === 'object' && 'data' in response) {
            return (response as ApiResponse<T>).data as T;
          }
          return response as T;
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError' || error.message?.includes('Timeout')) {
            const timeoutError = new Error('Request is taking too long. Please check your connection and try again.') as any;
            timeoutError.status = 408;
            return this.handleError(timeoutError);
          }
          return this.handleError(error);
        }),
      );
  }

  /**
   * Upload file (longer timeout for large files)
   */
  uploadFile(endpoint: string, file: File, folder?: string, makePublic?: boolean): Observable<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    if (makePublic) formData.append('makePublic', 'true');

    let headers = new HttpHeaders();
    const token = this.getAuthToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    // Don't set Content-Type for FormData - browser will set it with boundary

    return this.http
      .post<ApiResponse<{ url: string; key: string }> | { url: string; key: string }>(`${this.baseUrl}/${endpoint}`, formData, {
        headers,
      })
      .pipe(
        timeout(300000),
        map((response) => {
          if (response && typeof response === 'object' && 'data' in response) {
            return (response as ApiResponse<{ url: string; key: string }>).data as { url: string; key: string };
          }
          return response as { url: string; key: string };
        }),
        catchError((error) => {
          if (error.name === 'TimeoutError' || error.message?.includes('Timeout')) {
            const timeoutError = new Error('Upload is taking too long. Please check your connection and try again.') as any;
            timeoutError.status = 408;
            return this.handleError(timeoutError);
          }
          return this.handleError(error);
        }),
      );
  }

}

